import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Mail, MapPin } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import { useCopy } from '../context/SiteCopyContext';

const Footer = () => {
  const [email, setEmail] = React.useState('');
  const tagline = useCopy('footer.tagline', 'Solid timber. Premium bouclé. Furniture built the way it used to be — designed in Australia, made to outlive trends.');
  const ftEmail = useCopy('footer.email', 'info@timberandthread.com.au');
  const ftAddress = useCopy('footer.address', 'Melbourne, Australia');
  const newsletterTitle = useCopy('footer.newsletter_title', 'Easy access to new drops, restocks, and the occasional honest opinion.');
  const newsletterCta = useCopy('footer.newsletter_cta', 'Join →');

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      toast({
        title: 'Welcome to the family',
        description: 'You will hear from us soon — first dibs on new drops.',
      });
      setEmail('');
    } else {
      toast({
        title: 'Hmm, that does not look right',
        description: 'Please enter a valid email address.',
      });
    }
  };

  return (
    <footer className="bg-[#1E140B] text-[#E3D5B8] mt-24 grain">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 pt-24 pb-12 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-14 md:gap-10">
          {/* Brand */}
          <div className="md:col-span-5">
            <h3
              className="text-4xl md:text-5xl text-[#F5EDDD] mb-5 leading-none"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Timber <span className="italic text-[#A07A4F]">&amp;</span> Thread <span className="italic text-[#A07A4F]">co.</span>
            </h3>
            <p className="text-[#C9B596] text-[15px] leading-[1.85] max-w-md">
              {tagline}
            </p>

            <div className="mt-7 space-y-3 text-[13px] text-[#C9B596]">
              <p className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-[#A07A4F]" strokeWidth={1.4} /> {ftEmail}
              </p>
              <p className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-[#A07A4F]" strokeWidth={1.4} /> {ftAddress}
              </p>
            </div>

            <div className="flex items-center gap-3 mt-7">
              <a href="#" aria-label="Instagram" className="p-2.5 border border-[#3A2A1B] rounded-full hover:border-[#A07A4F] hover:text-white transition-colors">
                <Instagram className="h-4 w-4" strokeWidth={1.5} />
              </a>
              <a href="#" aria-label="Facebook" className="p-2.5 border border-[#3A2A1B] rounded-full hover:border-[#A07A4F] hover:text-white transition-colors">
                <Facebook className="h-4 w-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Links */}
          <div className="md:col-span-3 grid grid-cols-2 gap-x-8 gap-y-10">
            <div>
              <h4 className="text-[11px] tracking-[0.22em] uppercase text-[#A07A4F] mb-5">Shop</h4>
              <ul className="space-y-3 text-[14px]">
                <li><Link to="/collections" className="hover:text-white transition-colors">All collections</Link></li>
                <li><Link to="/collections/beds" className="hover:text-white transition-colors">All beds</Link></li>
                <li><Link to="/collections/sofas" className="hover:text-white transition-colors">Sofas</Link></li>
                <li><Link to="/collections#popular" className="hover:text-white transition-colors">What's popular</Link></li>
                <li><Link to="/collections/thread" className="hover:text-white transition-colors">Thread Collection</Link></li>
                <li><Link to="/collections/timber" className="hover:text-white transition-colors">Timber Collection</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[11px] tracking-[0.22em] uppercase text-[#A07A4F] mb-5">Services</h4>
              <ul className="space-y-3 text-[14px]">
                <li><Link to="/interior-styling" className="hover:text-white transition-colors">Interior Styling Service</Link></li>
                <li><Link to="/customisation" className="hover:text-white transition-colors">Customisation Service</Link></li>
                <li><Link to="/shopping-with-us" className="hover:text-white transition-colors">Shopping with us</Link></li>
                <li><Link to="/showrooms" className="hover:text-white transition-colors">Our showrooms</Link></li>
                <li><Link to="/refer-a-friend" className="hover:text-white transition-colors">Refer a Friend</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[11px] tracking-[0.22em] uppercase text-[#A07A4F] mb-5">Company</h4>
              <ul className="space-y-3 text-[14px]">
                <li><Link to="/about" className="hover:text-white transition-colors">Our story</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/sustainability" className="hover:text-white transition-colors">Sustainability</Link></li>
                <li><Link to="/journal" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/ambassador-program" className="hover:text-white transition-colors">Ambassador Program</Link></li>
                <li><Link to="/affiliate-program" className="hover:text-white transition-colors">Affiliate Program</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[11px] tracking-[0.22em] uppercase text-[#A07A4F] mb-5">Help</h4>
              <ul className="space-y-3 text-[14px]">
                <li><Link to="/faq#delivery" className="hover:text-white transition-colors">Delivery</Link></li>
                <li><Link to="/faq#warranty" className="hover:text-white transition-colors">Product Warranty</Link></li>
                <li><Link to="/faq#refunds" className="hover:text-white transition-colors">Sales &amp; Refunds</Link></li>
                <li><Link to="/faq#contact" className="hover:text-white transition-colors">Help Centre</Link></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
              </ul>
            </div>
          </div>

          {/* Newsletter */}
          <div className="md:col-span-4">
            <h4 className="text-[11px] tracking-[0.22em] uppercase text-[#A07A4F] mb-5">The List</h4>
            <p className="text-[14px] text-[#C9B596] mb-5 leading-relaxed">
              {newsletterTitle}
            </p>
            <form onSubmit={handleSubscribe} className="flex items-center gap-0 border-b border-[#3A2A1B] focus-within:border-[#A07A4F] transition-colors">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-transparent border-0 text-[#F5EDDD] placeholder:text-[#7A5A3A] rounded-none h-12 focus-visible:ring-0 focus-visible:ring-offset-0 px-0"
              />
              <Button
                type="submit"
                className="bg-transparent text-[#F5EDDD] hover:bg-transparent hover:text-[#A07A4F] rounded-none h-12 px-3 text-[11px] tracking-[0.22em] uppercase"
              >
                {newsletterCta}
              </Button>
            </form>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-[#3A2A1B] flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] text-[#7A5A3A]">
          <p>© {new Date().getFullYear()} Timber &amp; Thread co. Designed in Melbourne.</p>
          <div className="flex items-center gap-5">
            <Link to="/about" className="hover:text-[#C9B596] transition-colors">Privacy</Link>
            <Link to="/about" className="hover:text-[#C9B596] transition-colors">Terms</Link>
            <span>Australian Made Furniture</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
